import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ForgotPasswordService } from "@/services/forgot-password.service";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { AxiosErrorResponse } from "@/@types/Errors";
import { z } from "zod";

interface ResetPasswordData {
  password: string;
  password_confirmation: string;
}

const createResetPasswordSchema = (t: any) =>
  z
    .object({
      password: z
        .string()
        .min(8, { message: t("validation:password_min_length") })
        .regex(/(?=.*[a-z])/, { message: t("validation:password_lowercase") })
        .regex(/(?=.*[A-Z])/, { message: t("validation:password_uppercase") })
        .regex(/(?=.*\d)/, { message: t("validation:password_number") })
        .regex(/(?=.*[^a-zA-Z0-9])/, { message: t("validation:password_special_char") }),
      password_confirmation: z
        .string()
        .min(1, { message: t("common:required_field") }),
    })
    .refine((data) => data.password === data.password_confirmation, {
      path: ["password_confirmation"],
      message: t("validation:passwords_do_not_match"),
    });

const getTokenFromUrl = (): string | null => {
  return location.pathname.split("/").pop() || null;
};

const getEmailFromUrl = (): string | null => {
  return new URLSearchParams(location.search).get("email");
};

export const useResetPassword = () => {
  const { t } = useTranslation(["zod", "common", "validation"]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = getTokenFromUrl();
  const email = getEmailFromUrl();

  const ResetPasswordSchema = createResetPasswordSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordData>({
    resolver: zodResolver(ResetPasswordSchema),
  });

  const handleResetSuccess = () => {
    toast({
      variant: "success",
      description: t("common:password_reset_success"),
    });

    localStorage.setItem('@passwordResetSuccess', 'true');

    localStorage.removeItem('@email');
    localStorage.removeItem('@emailSent');

    navigate("/login", { replace: true });
  };

  const handleResetError = (error: AxiosError<AxiosErrorResponse>) => {
    if (error.response?.status === 422) {
      Object.values(error.response.data.errors).forEach((errorMessages: any) => {
        toast({
          variant: "error",
          description: errorMessages[0],
        });
      });
      return;
    }

    toast({
      variant: "error",
      description: error?.message || t("common:error_resetting_password"),
    });
  };

  const { mutate, isPending: isSubmitting, isError, isSuccess } = useMutation({
    mutationFn: async (data: ResetPasswordData) => {
      if (!token || !email) {
        throw new Error(t("common:invalid_reset_link"));
      }

      const payload = {
        email,
        token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      };

      await ForgotPasswordService.resetPassword(payload);
    },
    onSuccess: handleResetSuccess,
    onError: handleResetError,
  });

  const onSubmit = (data: ResetPasswordData) => {
    mutate(data);
  };

  return {
    onSubmit,
    handleSubmit,
    register,
    errors,
    isSubmitting,
    isError,
    isSuccess,
    email,
  };
};
